/** "Items with extracted clauses": the clause lists of a project (one per role, with what
 * each replaced and which of its requirements nobody assesses there) on its Files page,
 * and the Global data files with clause lists on Global data. Each opens its clause page,
 * a Global data file's under Global data. */
import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import { useGlobalRegisters, useProjectRegisters } from "../api";
import type { DocumentRegister } from "../api";
import { useAgentLabel } from "../api/hooks/useStatefulAgents";
import { clausePagePath } from "../routes/learningAgentPaths";
import { formatWhen } from "../utils/formatWhen";
import { ClauseGaps } from "./ClauseGaps";
import styles from "./Clauses.module.css";
import { registerChip, scopeText } from "./registerChip";

const cell = { padding: "6px 8px", borderTop: "1px solid #e5e7eb" } as const;
const note = { margin: "4px 0 0", fontSize: 12, color: "#6b7280" } as const;

const Row = ({
  register,
  projectId,
  scope,
  notes,
}: {
  register: DocumentRegister;
  /** The project the page is on; null on Global data. */
  projectId: string | null;
  scope: string;
  notes: ReactNode;
}) => {
  const { label, tone } = registerChip(register);
  const slot = register.slot;
  return (
    <tr>
      <td style={{ ...cell, whiteSpace: "nowrap", verticalAlign: "top" }}>
        {slot?.active
          ? slot.role_label
          : slot
            ? `${slot.role_label} (retired)`
            : register.status === "building"
              ? "Filed when built"
              : "No role"}
      </td>
      <td style={{ ...cell, overflowWrap: "anywhere" }}>
        <Link to={clausePagePath(register, projectId)}>{register.document_name}</Link>
        {slot && <p style={note}>Authoritative: {slot.authority}</p>}
        {notes}
      </td>
      <td style={cell}>{scope}</td>
      <td style={cell}>v{register.version}</td>
      <td style={cell}>{register.counts.requirements ?? 0}</td>
      <td style={cell}>
        <span className={`${styles.chip} ${tone}`}>{label}</span>
      </td>
      <td style={{ ...cell, whiteSpace: "nowrap" }}>
        {register.published_at ? formatWhen(register.published_at) : ""}
      </td>
    </tr>
  );
};

const Section = ({
  description,
  boxed,
  children,
  footer,
}: {
  description: string;
  /** A card of its own, among Global data's cards; the Files page's lead section is not. */
  boxed: boolean;
  children: ReactNode;
  footer?: ReactNode;
}) => (
  <section
    aria-labelledby="extracted-clauses-heading"
    className={boxed ? styles.card : undefined}
    style={
      boxed ? undefined : { marginBottom: 32, paddingBottom: 24, borderBottom: "2px solid #e5e7eb" }
    }
  >
    <h2
      id="extracted-clauses-heading"
      style={{ marginTop: 0, marginBottom: 4, fontSize: 18, color: "#1f2937" }}
    >
      Items with extracted clauses
    </h2>
    <p style={{ margin: "0 0 16px", fontSize: 13, color: "#6b7280" }}>{description}</p>
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
      <thead>
        <tr style={{ textAlign: "left", color: "#6b7280" }}>
          {["Role", "Document", "Scope", "Version", "Requirements", "Status", "Published"].map(
            (h) => (
              <th key={h} style={{ padding: "4px 8px" }}>
                {h}
              </th>
            ),
          )}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
    {footer}
  </section>
);

export const ExtractedClausesSection = ({ projectId }: { projectId: string }) => {
  const query = useProjectRegisters(projectId);
  const { agentLabel } = useAgentLabel();
  const data = query.data;
  const registers = [...(data?.registers ?? []), ...(data?.pending ?? [])];
  if (!data || registers.length === 0) return null;
  const topicLabel = (key: string) => data.topics.find((t) => t.key === key)?.label ?? key;
  return (
    <Section
      boxed={false}
      description="The binding documents in force on this project, broken into clauses."
      footer={
        data.shadowed.length > 0 && (
          <p style={note}>
            Not in force here, as a Global data list fills the same role:{" "}
            {data.shadowed.map((s) => `${s.document_name} (${s.role_label})`).join(", ")}
          </p>
        )
      }
    >
      {registers.map((r) => (
        <Row
          key={r.register_id}
          register={r}
          projectId={projectId}
          scope={r.document_kind === "global" ? "Global data" : "This project"}
          notes={
            <>
              {(data.replaced[r.document_id] ?? []).map((old) => (
                <p key={`${old.document_id}-${old.retired_at}`} style={note}>
                  Replaced {old.document_name}
                  {old.retired_at ? ` on ${formatWhen(old.retired_at)}` : ""}
                  {old.retired_reason ? `: ${old.retired_reason}` : ""}
                </p>
              ))}
              {data.gaps[r.document_id] && (
                <ClauseGaps
                  gaps={data.gaps[r.document_id]}
                  topicLabel={topicLabel}
                  agentLabel={agentLabel}
                />
              )}
            </>
          }
        />
      ))}
    </Section>
  );
};

export const GlobalExtractedClausesSection = () => {
  const documents = useGlobalRegisters().data?.documents ?? [];
  if (documents.length === 0) return null;
  return (
    <Section
      boxed
      description="Global data files and folders broken into clauses. Each applies to the projects in its scope.">
      {documents.map((d) => (
        <Row
          key={d.register_id}
          register={d}
          projectId={null}
          scope={scopeText(d.scope)}
          notes={
            <>
              {!d.shared && <p style={note}>No longer shared as Global data.</p>}
              {d.files.length > 0 && (
                <p style={note}>
                  A folder of {d.files.length} files: {d.files.map((f) => f.name).join(", ")}
                </p>
              )}
              {d.replaced.map((old) => (
                <p key={`${old.document_id}-${old.retired_at}`} style={note}>
                  Replaced {old.document_name}
                  {old.retired_at ? ` on ${formatWhen(old.retired_at)}` : ""}
                  {old.retired_reason ? `: ${old.retired_reason}` : ""}
                </p>
              ))}
              {d.newest_status !== d.status && (
                <p style={note}>
                  A newer version{" "}
                  {d.newest_status === "building" ? "is being built" : "failed to build"}.
                </p>
              )}
            </>
          }
        />
      ))}
    </Section>
  );
};
